import { defineStore } from "pinia"
import type { CourseOption } from "@/components/WCourseSelector/types"
import { getCourseListWithCat } from "@/api/teacher"

export const useCourseStore = defineStore("course", {
  state: () => ({
    selectedCourseId: null as number | null,
    selectedCourseInfo: null as CourseOption | null,
    courseOptions: [] as CourseOption[],
    loading: false,
    initialized: false,
    isInitializing: false // 添加初始化状态标志
  }),

  getters: {
    // 获取当前选中的课程名称
    selectedCourseName: (state) => {
      return state.selectedCourseInfo?.label || "请选择课程"
    },

    // 获取所有可选择的课程（排除分类）
    availableCourses: (state) => {
      const courses: CourseOption[] = []

      const extractCourses = (options: CourseOption[]) => {
        options.forEach((option) => {
          if (!option.isCategory && option.courseId) {
            courses.push(option)
          }
          if (option.children && option.children.length > 0) {
            extractCourses(option.children)
          }
        })
      }

      extractCourses(state.courseOptions)
      return courses
    },

    // 获取第一个可用课程
    firstAvailableCourse: (state) => {
      const courses = state.courseOptions

      const findFirstCourse = (options: CourseOption[]): CourseOption | null => {
        for (const option of options) {
          if (!option.isCategory && option.courseId) {
            return option
          }
          if (option.children && option.children.length > 0) {
            const found = findFirstCourse(option.children)
            if (found) return found
          }
        }
        return null
      }

      return findFirstCourse(courses)
    }
  },

  actions: {
    // 设置选中的课程
    async setSelectedCourse(courseId: number | null, courseInfo?: CourseOption, skipPermissionRefresh?: boolean) {
      const previousCourseId = this.selectedCourseId

      console.log("setSelectedCourse 调用", {
        previousCourseId,
        newCourseId: courseId,
        skipPermissionRefresh,
        courseInfo: courseInfo?.label
      })

      this.selectedCourseId = courseId
      this.selectedCourseInfo = courseInfo || null

      // 保存到localStorage
      if (courseId && courseInfo) {
        localStorage.setItem("selectedCourseId", courseId.toString())
        localStorage.setItem("selectedCourseInfo", JSON.stringify(courseInfo))
      } else {
        localStorage.removeItem("selectedCourseId")
        localStorage.removeItem("selectedCourseInfo")
      }

      // 只有当课程真正发生变化且不跳过权限刷新时，才刷新权限菜单
      const shouldRefreshPermissions = previousCourseId !== courseId && !skipPermissionRefresh
      console.log("是否需要刷新权限:", shouldRefreshPermissions)

      if (shouldRefreshPermissions) {
        console.log("课程发生变化，执行重新登录式的状态重置", {
          from: previousCourseId,
          to: courseId
        })

        // 🚀 重新登录式的状态重置
        await this.resetApplicationState()

        if (courseId) {
          // 只有选中了有效课程才刷新权限
          try {
            const router = (await import("@/router/index")).default
            const currentRoute = router.currentRoute.value
            const currentPath = currentRoute.path

            console.log("课程切换前的当前页面路径:", currentPath)

            const { refreshPermissionMenus } = await import("@/api/routes")
            await refreshPermissionMenus(courseId)

            // 🎯 检查当前页面是否有权限，决定是否需要跳转
            const hasCurrentPagePermission = await this.checkCurrentPagePermission(currentPath, courseId)

            if (hasCurrentPagePermission) {
              console.log("当前页面有权限，保持在当前页面:", currentPath)

              // 🎯 即使页面有权限，也需要刷新页面数据以显示新课程的数据
              try {
                // 触发页面刷新事件，让页面重新加载数据
                const router = (await import("@/router/index")).default
                const currentRoute = router.currentRoute.value

                // 通过路由跳转触发页面刷新（使用replace避免历史记录）
                await router.replace({
                  path: currentRoute.path,
                  query: {
                    ...currentRoute.query,
                    _t: Date.now() // 添加时间戳强制刷新
                  }
                })

                console.log("页面已刷新，显示新课程数据")
              } catch (refreshError) {
                console.warn("页面刷新失败:", refreshError)
              }

              // 显示成功提示
              try {
                const { ElMessage } = await import("element-plus")
                ElMessage.success(`已切换到课程：${courseInfo?.label || ""}`)
              } catch (msgError) {
                console.warn("无法显示成功提示:", msgError)
              }
            } else {
              console.log("当前页面没有权限，跳转到第一个有权限的页面")
              const { getFirstAvailablePagePath } = await import("@/api/routes")
              const firstPagePath = await getFirstAvailablePagePath()

              await router.push(firstPagePath)

              // 显示成功提示（包含跳转信息）
              try {
                const { ElMessage } = await import("element-plus")
                ElMessage.success(`已切换到课程：${courseInfo?.label || ""}，并跳转到有权限的页面`)
              } catch (msgError) {
                console.warn("无法显示成功提示:", msgError)
              }
            }
          } catch (error) {
            console.error("课程变化时刷新权限菜单失败:", error)

            // 显示权限获取失败的提示
            const errorMessage = error?.response?.data?.message || error.message || "权限菜单加载失败"
            try {
              const { ElMessage } = await import("element-plus")
              ElMessage.error(`权限菜单加载失败: ${errorMessage}`)
            } catch (msgError) {
              console.warn("无法显示错误提示:", msgError)
            }
          }
        } else {
          // 清空课程时，清空权限菜单
          console.log("清空课程，清空权限菜单")
          try {
            const { usePermissionStoreHook } = await import("@/store/modules/permission")
            usePermissionStoreHook().clearAllCachePage()
          } catch (error) {
            console.error("清空权限菜单失败:", error)
          }
        }
      } else {
        console.log("跳过权限菜单刷新")
      }
    },

    // 设置课程选项数据
    setCourseOptions(options: CourseOption[]) {
      this.courseOptions = options
    },

    // 从localStorage恢复状态
    restoreFromStorage() {
      try {
        const savedCourseId = localStorage.getItem("selectedCourseId")
        const savedCourseInfo = localStorage.getItem("selectedCourseInfo")

        if (savedCourseId && savedCourseInfo) {
          const courseId = parseInt(savedCourseId)
          const courseInfo = JSON.parse(savedCourseInfo) as CourseOption

          this.selectedCourseId = courseId
          this.selectedCourseInfo = courseInfo

          return true
        }
      } catch (error) {
        console.error("CourseStore - 恢复状态失败:", error)
        // 清除无效的localStorage数据
        localStorage.removeItem("selectedCourseId")
        localStorage.removeItem("selectedCourseInfo")
      }
      return false
    },

    // 初始化课程数据
    async initializeCourses(skipPermissionRefresh = false) {
      // 防止重复初始化
      if (this.initialized && this.courseOptions.length > 0) {
        console.log("课程已初始化，跳过重复初始化")
        return
      }

      if (this.isInitializing) {
        console.log("课程正在初始化中，跳过重复调用")
        return
      }

      this.isInitializing = true
      this.loading = true

      try {
        console.log("=== 开始初始化课程数据 ===")

        // 加载课程数据
        const response = await getCourseListWithCat({})
        console.log("获取到的课程数据:", response)

        // 把 response 即使为 null 也当做正常响应处理，因为后端没有数据时会返回 null
        // 转换数据格式（复用WCourseSelector的转换逻辑）
        const transformedData = this.transformCourseData(response)
        console.log("转换后的课程数据:", transformedData)

        this.setCourseOptions(transformedData)

        // 检查是否有可用的课程
        const availableCourses = this.availableCourses
        console.log("可用课程数量:", availableCourses.length)

        if (availableCourses.length === 0) {
          // 没有课程时的提示
          console.warn("当前暂无授权的课程")
          try {
            const { ElMessage } = await import("element-plus")
            ElMessage.warning("当前暂无授权的课程，请联系管理员！")
          } catch (msgError) {
            console.warn("无法显示课程警告提示:", msgError)
          }
          this.initialized = true
          return
        }

        // 先尝试从localStorage恢复状态
        const restored = this.restoreFromStorage()
        console.log("从localStorage恢复状态:", restored)

        // 如果没有从localStorage恢复状态，或者恢复的课程在新数据中不存在，则选择第一个课程
        if (!restored || !this.validateSelectedCourse()) {
          console.log("选择第一个可用课程")
          await this.selectFirstAvailableCourse(skipPermissionRefresh)
        } else {
          console.log("使用已恢复的课程")
          if (!skipPermissionRefresh) {
            console.log("手动触发权限更新")
            // 如果恢复了课程状态，手动刷新权限菜单（不通过setSelectedCourse避免重复）
            try {
              console.log("手动刷新权限菜单，课程ID:", this.selectedCourseId)
              const { refreshPermissionMenus } = await import("@/api/routes")
              await refreshPermissionMenus(this.selectedCourseId || undefined)
            } catch (error) {
              console.error("恢复课程状态时刷新权限菜单失败:", error)
            }
          } else {
            console.log("跳过权限刷新（登录时）")
          }
        }

        this.initialized = true
      } catch (error) {
        console.error("CourseStore - 初始化课程数据失败:", error)

        // 显示错误提示
        const errorMessage = error?.response?.data?.message || error.message || "初始化课程数据失败"
        try {
          const { ElMessage } = await import("element-plus")
          ElMessage.error(`课程数据获取失败: ${errorMessage}`)
        } catch (msgError) {
          console.warn("无法显示错误提示:", msgError)
        }
      } finally {
        this.loading = false
        this.isInitializing = false
      }
    },

    // 选择第一个可用课程
    async selectFirstAvailableCourse(skipPermissionRefresh = false) {
      const firstCourse = this.firstAvailableCourse
      if (firstCourse) {
        await this.setSelectedCourse(firstCourse.courseId!, firstCourse, skipPermissionRefresh)
      } else {
      }
    },

    // 验证当前选中的课程是否仍然有效
    validateSelectedCourse(): boolean {
      if (!this.selectedCourseId || !this.selectedCourseInfo) {
        return false
      }

      // 检查当前选中的课程是否在新的课程列表中
      const availableCourses = this.availableCourses
      const isValid = availableCourses.some((course) => course.courseId === this.selectedCourseId)

      if (!isValid) {
        // 注意：这里不用await，因为validateSelectedCourse不是async
        this.setSelectedCourse(null)
      }

      return isValid
    },

    // 检查当前页面在新课程权限中是否存在
    async checkCurrentPagePermission(currentPath: string, courseId: number): Promise<boolean> {
      try {
        console.log("检查页面权限:", currentPath, "课程ID:", courseId)

        // 🎯 优化：使用store中已有的权限数据，避免重复请求
        const { usePermissionStoreHook } = await import("@/store/modules/permission")
        const permissionStore = usePermissionStoreHook()
        const permissionList = permissionStore.permissions || []

        console.log("使用store中的权限数据，权限数量:", permissionList.length)

        if (!permissionList || permissionList.length === 0) {
          console.log("store中没有权限数据，默认有权限（避免误判）")
          return true
        }

        // 检查当前路径是否在权限列表中
        const hasPermission = permissionList.some((permission) => {
          // 检查 routePath 是否匹配
          if (permission.routePath && permission.routePath === currentPath) {
            console.log("找到匹配的权限 (routePath):", permission.menuName, permission.routePath)
            return true
          }

          // 检查根据 perms 生成的路径是否匹配
          if (permission.perms) {
            const generatedPath = "/" + permission.perms.replace(/:/g, "/")
            if (generatedPath === currentPath) {
              console.log("找到匹配的权限 (perms):", permission.menuName, generatedPath)
              return true
            }
          }

          return false
        })

        // 特殊处理：工作台路径通常都有权限
        if (currentPath === "/dashboard" || currentPath === "/") {
          console.log("工作台页面，默认有权限")
          return true
        }

        console.log("当前页面权限检查结果:", hasPermission)
        return hasPermission
      } catch (error) {
        console.error("检查当前页面权限失败:", error)
        // 出错时默认有权限，避免误判导致跳转
        return true
      }
    },

    // 重置应用状态（像重新登录一样）
    async resetApplicationState() {
      console.log("=== 开始重置应用状态（重新登录式） ===")

      try {
        // 1. 清空多标签页，重置为初始状态
        console.log("1. 清空多标签页")
        const { useMultiTagsStoreHook } = await import("@/store/modules/multiTags")
        const { routerArrays } = await import("@/store/utils")
        useMultiTagsStoreHook().handleTags("equal", [...routerArrays])

        // 2. 清空权限缓存和菜单
        console.log("2. 清空权限缓存和菜单")
        const { usePermissionStoreHook } = await import("@/store/modules/permission")
        const permissionStore = usePermissionStoreHook()
        permissionStore.clearAllCachePage()

        // 3. 重置路由状态
        console.log("3. 重置路由状态")
        const { resetRouter } = await import("@/router/index")
        resetRouter()

        // 4. 清空组件缓存
        console.log("4. 清空组件缓存")
        // 清空所有页面组件的缓存状态
        permissionStore.cachePageList = []

        // 5. 重置菜单初始化状态
        console.log("5. 重置菜单初始化状态")
        permissionStore.menuInitialized = false

        // 6. 清空试题篮
        console.log("6. 清空试题篮")
        const { usePaperStoreHook } = await import("@/store/pages/paper")
        usePaperStoreHook().clearBasket()

        console.log("=== 应用状态重置完成 ===")
      } catch (error) {
        console.error("重置应用状态失败:", error)
        throw error
      }
    },

    // 清理课程数据（用户退出时调用）
    clearCourseData() {
      console.log("清理课程数据")
      this.selectedCourseId = null
      this.selectedCourseInfo = null
      this.courseOptions = []
      this.loading = false
      this.initialized = false
      this.isInitializing = false

      // 清理localStorage中的课程信息
      try {
        localStorage.removeItem("selectedCourseId")
        localStorage.removeItem("selectedCourseInfo")
        localStorage.removeItem("courseOptions")
        console.log("课程数据已清理")
      } catch (error) {
        console.warn("清理localStorage中的课程数据失败:", error)
      }
    },

    // 课程数据转换逻辑（从WCourseSelector复制）
    transformCourseData(data: any): CourseOption[] {
      if (!data || !data.id) {
        return []
      }

      return this.flattenCategoryData(data)
    },

    // 扁平化处理分类数据
    flattenCategoryData(item: any): CourseOption[] {
      const result: CourseOption[] = []

      // 如果根节点有课程，添加一个根分类
      if (item.courses && item.courses.length > 0) {
        const rootCategory: CourseOption = {
          label: item.name,
          value: `cat_${item.id}`,
          disabled: true,
          isCategory: true,
          categoryId: item.id,
          children: item.courses.map((course: any) => ({
            label: course.name,
            value: course.id,
            disabled: false,
            isCategory: false,
            courseId: course.id,
            courseData: course
          }))
        }
        result.push(rootCategory)
      }

      // 如果有子分类，则展开显示子分类
      if (item.children && item.children.length > 0) {
        item.children.forEach((child: any) => {
          result.push(this.transformSingleCatData(child))
        })
      }

      return result
    },

    // 转换单个分类数据
    transformSingleCatData(item: any): CourseOption {
      const categoryItem: CourseOption = {
        label: item.name,
        value: `cat_${item.id}`,
        disabled: true,
        children: [],
        isCategory: true,
        categoryId: item.id
      }

      // 添加该分类下的课程
      if (item.courses && item.courses.length > 0) {
        categoryItem.children!.push(
          ...item.courses.map((course: any) => ({
            label: course.name,
            value: course.id,
            disabled: false,
            isCategory: false,
            courseId: course.id,
            courseData: course
          }))
        )
      }

      // 递归添加子分类
      if (item.children && item.children.length > 0) {
        categoryItem.children!.push(...item.children.map((child: any) => this.transformSingleCatData(child)))
      }

      return categoryItem
    },

    // 清除选中状态
    async clearSelection() {
      await this.setSelectedCourse(null)
    }
  }
})
